import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config.js';
import SuggestedQuestions from './SuggestedQuestions.jsx';
import ClarificationChips from './ClarificationChips.jsx';
import './ChatMessage.css';

/**
 * Component for displaying individual chat messages
 * @param {Object} props - Component props
 * @param {string} props.role - 'user' or 'assistant'
 * @param {string} props.content - Message content
 * @param {string} props.timestamp - ISO timestamp
 * @param {Object} props.metadata - Additional message data
 * @param {Function} props.onClick - Click handler for rerunning query
 * @param {boolean} props.isSelected - Whether this message is currently selected
 * @param {string} props.messageId - ID of the message
 * @param {boolean} props.showGraphButton - Whether to show graph button (default: false)
 * @param {Function} props.onGraphClick - Callback when graph button is clicked
 * @param {Function} props.onSuggestedQuestion - Callback when a follow-up chip is clicked
 * @param {boolean} [props.hideTimestamp=false] - Hide the message timestamp
 * @param {boolean} [props.suggestionsDisabled=false] - Disable follow-up / welcome chips while loading
 * @returns {JSX.Element} Chat message component
 */
const ChatMessage = ({ role, content, timestamp, metadata, onClick, isSelected, messageId, showGraphButton = false, onGraphClick, onSuggestedQuestion, hideTimestamp = false, suggestionsDisabled = false }) => {
  const { t } = useTranslation();
  const isUser = role === 'user';
  const isError = metadata?.error;

  // Support both legacy scalar queryId and new array form
  const hasQueryId = Array.isArray(metadata?.queryId)
    ? metadata.queryId.some(id => id != null)
    : !!metadata?.queryId;

  const isClickable = !isUser && hasQueryId && !isError;

  // Support both legacy scalar data and new array form
  const hasChartData = !isError && (
    Array.isArray(metadata?.data)
      ? metadata.data.some(d => d != null)
      : !!metadata?.data
  );

  // First non-null step — used for the metadata footer (time period, sources)
  const firstStepData = Array.isArray(metadata?.data)
    ? metadata.data.find(d => d != null)
    : metadata?.data;

  const handleClick = () => {
    if (isClickable && onClick) {
      const queryIdArr = metadata.queryId;
      if (Array.isArray(queryIdArr)) {
        const firstIdx = queryIdArr.findIndex(id => id != null);
        if (firstIdx !== -1) {
          onClick(messageId, queryIdArr[firstIdx], firstIdx);
        }
      } else {
        onClick(messageId, queryIdArr, 0);
      }
    }
  };
  
  const formatTimestamp = (timestamp) => {
    const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';
    return new Date(timestamp).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimePeriod = (timePeriod, granularity) => {
    if (!timePeriod) return '';
    
    const { start_date, end_date } = timePeriod;
    
    // Format for weekly data
    if (granularity === 'weekly' || granularity === '4-week periods') {
      try {
        const startDate = new Date(start_date);
        const endDate = new Date(end_date);
        
        // Get ISO week numbers
        const getWeekNumber = (date) => {
          const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
          const dayNum = d.getUTCDay() || 7;
          d.setUTCDate(d.getUTCDate() + 4 - dayNum);
          const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
          return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
        };
        
        const startWeek = getWeekNumber(startDate);
        const endWeek = getWeekNumber(endDate);
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        
        const weekLabel = i18n.language === 'no' ? 'Uke' : 'Week';
        
        if (startYear === endYear) {
          return `${weekLabel} ${startWeek}-${endWeek} ${startYear}`;
        } else {
          return `${weekLabel} ${startWeek} ${startYear} - ${weekLabel} ${endWeek} ${endYear}`;
        }
      } catch (e) {
        // Fallback to default formatting
        return `${start_date} - ${end_date}`;
      }
    }
    
    // Default formatting for other granularities
    return `${start_date} - ${end_date}`;
  };

  // Function to detect and format data lists as tables
  const formatContentWithTables = (text) => {
    if (isUser) return text; // Don't format user messages
    
    const lines = text.split('\n');
    
    // Try to detect multi-column table pattern with pipe separators
    // Pattern: "- Label1: Value1 | Label2: Value2 | Label3: Value3"
    const pipeTableMatch = detectPipeTable(lines);
    if (pipeTableMatch) {
      return renderMultiColumnTable(pipeTableMatch.headers, pipeTableMatch.rows, pipeTableMatch.otherLines);
    }
    
    // Fallback: detect simple two-column pattern
    // Pattern: "- Name: Amount NOK"
    const simpleTableMatch = detectSimpleTable(lines);
    if (simpleTableMatch) {
      return renderSimpleTable(simpleTableMatch.rows, simpleTableMatch.otherLines);
    }
    
    // Format text with proper structure (paragraphs, lists, etc.)
    return formatTextWithStructure(text);
  };

  // Parse bold markdown (**text**) in a string
  const parseBoldText = (text) => {
    const parts = [];
    let lastIndex = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match;
    let key = 0;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the bold part
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add bold text
      parts.push(<strong key={`bold-${key++}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  // Format text content with proper paragraphs, lists, and structure
  const formatTextWithStructure = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let currentList = [];
    let currentListType = null; // 'bullet' or 'numbered'
    let key = 0;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join(' ');
        elements.push(
          <p key={`p-${key++}`} style={{ marginBottom: '12px' }}>
            {parseBoldText(paragraphText)}
          </p>
        );
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        const ListTag = currentListType === 'numbered' ? 'ol' : 'ul';
        elements.push(
          <ListTag key={`list-${key++}`} style={{ marginBottom: '12px', marginLeft: '20px' }}>
            {currentList.map((item, idx) => (
              <li key={`li-${key}-${idx}`} style={{ marginBottom: '4px' }}>
                {parseBoldText(item)}
              </li>
            ))}
          </ListTag>
        );
        currentList = [];
        currentListType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Empty line - signals paragraph break
      if (!trimmedLine) {
        flushParagraph();
        flushList();
        return;
      }

      // Detect bullet points (-, *, •)
      const bulletMatch = trimmedLine.match(/^[-*•]\s+(.+)$/);
      if (bulletMatch) {
        flushParagraph();
        if (currentListType !== 'bullet') {
          flushList();
          currentListType = 'bullet';
        }
        currentList.push(bulletMatch[1]);
        return;
      }

      // Detect numbered lists (1., 2., etc.)
      const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/);
      if (numberedMatch) {
        flushParagraph();
        if (currentListType !== 'numbered') {
          flushList();
          currentListType = 'numbered';
        }
        currentList.push(numberedMatch[1]);
        return;
      }

      // Detect Markdown headers (# / ## / ###). Map # and ## → h2, ###+ → h3
      // so chat layout never gets a page-level h1. Table heuristics run first
      // and are unaffected.
      const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        flushParagraph();
        flushList();
        const level = headerMatch[1].length;
        const Tag = level >= 3 ? 'h3' : 'h2';
        elements.push(
          <Tag key={`h-${key++}`} className={`message-heading message-heading-${Tag}`}>
            {parseBoldText(headerMatch[2])}
          </Tag>
        );
        return;
      }

      // Regular text - add to current paragraph
      flushList();
      currentParagraph.push(trimmedLine);
    });

    // Flush any remaining content
    flushParagraph();
    flushList();

    return elements.length > 0 ? <div>{elements}</div> : text;
  };

  // Detect pipe-separated multi-column tables
  const detectPipeTable = (lines) => {
    const dataLines = [];
    const otherLines = [];
    let headers = null;
    
    lines.forEach(line => {
      // Match patterns like "- Date: 2025-01 | Revenue: 50,000 NOK | Expenses: 25,000 NOK | Net: 25,000 NOK"
      const pipeMatch = line.match(/^-\s*(.+)\s*\|\s*(.+)/);
      if (pipeMatch) {
        // Split by pipe and extract label: value pairs
        const segments = line.substring(1).split('|').map(s => s.trim());
        const rowData = {};
        
        segments.forEach(segment => {
          const colonMatch = segment.match(/^([^:]+):\s*(.+)$/);
          if (colonMatch) {
            const label = colonMatch[1].trim();
            const value = colonMatch[2].trim();
            rowData[label] = value;
            
            // Build headers from first matching line
            if (!headers) {
              headers = [];
            }
            if (dataLines.length === 0 && !headers.includes(label)) {
              headers.push(label);
            }
          }
        });
        
        if (Object.keys(rowData).length > 0) {
          dataLines.push(rowData);
        }
      } else {
        otherLines.push(line);
      }
    });
    
    if (dataLines.length > 0 && headers && headers.length > 1) {
      return { headers, rows: dataLines, otherLines };
    }
    
    return null;
  };

  // Detect simple two-column tables
  const detectSimpleTable = (lines) => {
    const dataLines = [];
    const otherLines = [];
    
    lines.forEach(line => {
      // Match patterns like "- Oslo Street Food: 850,355.73 NOK" or "- April 2025: 2,041,827.25 NOK"
      const dataMatch = line.match(/^-\s*([^:]+):\s*([\d,]+\.?\d*)\s*(NOK|USD|EUR|\$)?/);
      if (dataMatch) {
        dataLines.push({
          name: dataMatch[1].trim(),
          amount: dataMatch[2],
          currency: dataMatch[3] || 'NOK'
        });
      } else {
        otherLines.push(line);
      }
    });
    
    if (dataLines.length > 0) {
      return { rows: dataLines, otherLines };
    }
    
    return null;
  };

  // Render multi-column table
  const renderMultiColumnTable = (headers, rows, otherLines) => {
    const prose = otherLines.join('\n');
    return (
      <div>
        {prose.trim() ? formatTextWithStructure(prose) : null}
        <div className="message-table-container">
          <table className="message-table">
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((header, colIndex) => (
                    <td key={colIndex}>{row[header] || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render simple two-column table
  const renderSimpleTable = (rows, otherLines) => {
    const prose = otherLines.join('\n');
    return (
      <div>
        {prose.trim() ? formatTextWithStructure(prose) : null}
        <div className="message-table-container">
          <table className="message-table">
            <thead>
              <tr>
                <th>Navn</th>
                <th>Beløp</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>{row.name}</td>
                  <td>{row.amount} {row.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div 
      className={`chat-message ${isUser ? 'user' : 'assistant'} ${isError ? 'error' : ''} ${isClickable ? 'clickable' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
    >
      <div className="message-header">
        <div className="message-header-left">
          {isUser ? (
            <span className="message-role">
              {t('chat.you')}
            </span>
          ) : (
            <span className="message-role">
              {t('chat.revetall')}
            </span>
          )}
        </div>
        <div className="message-header-right">
          {!hideTimestamp && timestamp && (
            <span className="message-time">
              {formatTimestamp(timestamp)}
            </span>
          )}
        </div>
      </div>
      
      <div className="message-content">
        {typeof content === 'string' ? formatContentWithTables(content) : content}
      </div>

      {!isUser && !isError && metadata?.clarification?.fields?.length > 0 && (
        <div className="message-clarification">
          <ClarificationChips
            fields={metadata.clarification.fields}
            onSubmit={onSuggestedQuestion}
            disabled={suggestionsDisabled}
          />
        </div>
      )}

      {!isUser && !isError && metadata?.followUpQuestions?.length > 0 && (
        <div
          className={`message-follow-ups${showGraphButton && hasChartData ? ' has-graph-button' : ''}`}
        >
          <SuggestedQuestions
            intro={metadata.followUpIntro || t('chat.followUpIntro')}
            questions={metadata.followUpQuestions}
            onSelect={onSuggestedQuestion}
            disabled={suggestionsDisabled}
          />
        </div>
      )}
      
      {/* Metadata footer + graph buttons */}
      {!isUser && (firstStepData?.metadata || (showGraphButton && hasChartData)) && (
        <div className="message-metadata">
          {firstStepData?.metadata?.time_period && (
            <span className="metadata-item">
              <span className="metadata-label">{t('messages.timePeriod')}</span>{' '}
              {formatTimePeriod(firstStepData.metadata.time_period, firstStepData.metadata.granularity)}
            </span>
          )}
          {firstStepData?.metadata?.data_sources?.length > 0 && (
            <span className="metadata-item">
              <span className="metadata-label">{t('messages.sources')}</span>{' '}
              {firstStepData.metadata.data_sources.join(', ')}
            </span>
          )}
          {showGraphButton && hasChartData &&
            (Array.isArray(metadata?.data) ? metadata.data : [metadata?.data]).map((stepData, i) =>
              stepData ? (
                <button
                  key={i}
                  className="message-graph-button"
                  onClick={(e) => { e.stopPropagation(); onGraphClick && onGraphClick(messageId, i); }}
                  title={t('messages.seeVisualization')}
                  aria-label={t('messages.seeVisualization')}
                >
                  📈
                </button>
              ) : null
            )
          }
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
